// src/app/services/qr-dialog.service.ts

import { inject, Injectable } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { QRCodeConfig, QRCodeOptions } from '@zayan-erp/sales';
import { QrCodeDialogComponent } from '../components/qr-code-dialog/qr-code-dialog.component';

@Injectable({
  providedIn: 'root'
})
export class QrDialogService {
  #modalService = inject(NgbModal);

  /**
   * Opens a QR code in an NgBootstrap modal with automatic optimization
   */
  showQRCode(config: QRCodeConfig): NgbModalRef {
    // Auto-optimize options based on data type and size
    const finalConfig = { ...config };

    if (!finalConfig.options) {
      finalConfig.options = this.getOptimalOptions(config.data);
    }

    const modalRef = this.#modalService.open(QrCodeDialogComponent, {
      size: 'md',
      centered: true,
      backdrop: 'static',
      keyboard: true
    });

    // Set the config as an input to the component
    modalRef.componentInstance.config = finalConfig;

    return modalRef;
  }

  /**
   * Convenience method to show a URL QR code
   */
  showUrlQR(url: string, title?: string): NgbModalRef {
    return this.showQRCode({
      data: { type: 'url', content: url },
      popupTitle: title || 'Website QR Code',
      label: 'Scan to visit website',
      showDownload: true
    });
  }

  /**
   * Convenience method to show an email QR code
   */
  showEmailQR(email: string, subject?: string, body?: string, title?: string): NgbModalRef {
    return this.showQRCode({
      data: {
        type: 'email',
        content: { email, subject, body }
      },
      popupTitle: title || 'Email QR Code',
      label: 'Scan to send email',
      showDownload: true
    });
  }

  /**
   * Convenience method to show a phone QR code
   */
  showPhoneQR(phone: string, title?: string): NgbModalRef {
    return this.showQRCode({
      data: { type: 'phone', content: phone },
      popupTitle: title || 'Phone QR Code',
      label: 'Scan to call',
      showDownload: true
    });
  }

  /**
   * Convenience method to show a WiFi QR code
   */
  showWifiQR(ssid: string, password: string, title?: string): NgbModalRef {
    return this.showQRCode({
      data: {
        type: 'wifi',
        content: { ssid, password, security: 'WPA', hidden: false }
      },
      popupTitle: title || 'WiFi QR Code',
      label: `Scan to connect to ${ssid}`,
      showDownload: true
    });
  }

  /**
   * Convenience method to show a text QR code
   */
  showTextQR(text: string, title?: string): NgbModalRef {
    return this.showQRCode({
      data: { type: 'text', content: text },
      popupTitle: title || 'Text QR Code',
      label: 'Scan to view text',
      showDownload: true
    });
  }

  /**
   * Convenience method to show a vCard QR code
   */
  showVCardQR(contact: any, title?: string): NgbModalRef {
    return this.showQRCode({
      data: { type: 'vcard', content: contact },
      popupTitle: title || 'Contact QR Code',
      label: 'Scan to save contact',
      showDownload: true
    });
  }

  /**
   * Get optimal QR code options based on data type and estimated size
   */
  private getOptimalOptions(data: any): QRCodeOptions {
    const estimatedLength = this.estimateDataLength(data);

    const options: QRCodeOptions = {
      margin: 2
    };

    if (estimatedLength > 1300) {
      // Large data - use low error correction and large size
      options.errorCorrectionLevel = 'L';
      options.width = 400;
    } else if (estimatedLength > 800) {
      // Medium data
      options.errorCorrectionLevel = 'M';
      options.width = 300;
    } else {
      // Small data - can use higher error correction
      options.errorCorrectionLevel = 'H';
      options.width = 256;
    }

    return options;
  }

  /**
   * Rough estimation of final QR data length
   */
  private estimateDataLength(data: any): number {
    switch (data.type) {
      case 'url':
        return data.content.length + 10; // Account for https prefix
      case 'email':
        if (typeof data.content === 'string') {
          return data.content.length + 7; // mailto:
        }
        const { email, subject = '', body = '' } = data.content;
        return email.length + subject.length + body.length + 20; // Account for mailto formatting
      case 'vcard':
        // Estimate vCard size
        const contact = data.content;
        return Object.values(contact).join('').length + 100; // Account for vCard formatting
      case 'wifi':
        const wifi = data.content;
        return wifi.ssid.length + wifi.password.length + 50; // Account for WIFI formatting
      default:
        return data.content.toString().length;
    }
  }
}
